import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ── Palette ──────────────────────────────────────────────────────────────────
const COLOR = {
  titleBg: "1E293B", // slate banner
  titleFg: "FFFFFF",
  zebra: "F1F5F9", // alt data row
  quotaHit: "DCFCE7", // >=99% quota → light green row
  totalBg: "E2E8F0", // grand-total slate band
  totalFg: "0F172A",
  totalTop: "94A3B8", // medium top border on the totals row
  adjBg: "FEF3C7", // amber
  adjFg: "92400E",
  bonusBg: "EFF6FF", // light blue for bonus
  bonusFg: "1E40AF",
  finalBg: "DCFCE7", // green final-total
  finalFg: "14532D",
  sectionBg: "475569", // slate sub-header that labels each DM on the All sheet
  sectionFg: "FFFFFF",
  borderColor: "CBD5E1",
};

// Group color bands
const GROUP_META = {
  Identity: { band: "E2E8F0", head: "F1F5F9", text: "334155" },
  "Store Performance": { band: "DBEAFE", head: "EFF6FF", text: "1E40AF" },
  "DM Commission": { band: "EDE9FE", head: "F5F3FF", text: "5B21B6" },
  Total: { band: "FEF3C7", head: "FFFBEB", text: "92400E" },
};

const FONT = "Calibri";
const FONT_SZ = {
  title: 13,
  band: 11,
  head: 10,
  data: 11,
  total: 11,
  final: 11,
};
const ROW_HPT = { title: 28, band: 24, head: 30, data: 20 };

// ── Number formats (accounting-friendly) ────────────────────────────────────
const CUR_FMT = "$#,##0.00_);[Red]($#,##0.00)";
const PCT_FMT = "0.00%";
const NUM_FMT = "0.00";
const ACCT_FMT =
  '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';

const QUOTA_HILITE = 99;

// ── Helpers ──────────────────────────────────────────────────────────────────
const fill = (hex) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: hex },
});

const border = () => ({
  top: { style: "thin", color: { argb: COLOR.borderColor } },
  bottom: { style: "thin", color: { argb: COLOR.borderColor } },
  left: { style: "thin", color: { argb: COLOR.borderColor } },
  right: { style: "thin", color: { argb: COLOR.borderColor } },
});

const borderTop = (topHex) => ({
  ...border(),
  top: { style: "medium", color: { argb: topHex } },
});

const applyDataStyle = (cell, opts = {}) => {
  cell.font = {
    name: FONT,
    size: opts.size || FONT_SZ.data,
    bold: opts.bold || false,
    color: { argb: opts.color || "000000" },
  };
  cell.fill = fill(opts.bg || "FFFFFF");
  cell.border = opts.border || border();
  cell.alignment = { horizontal: opts.align || "left", vertical: "middle" };
  if (opts.numFmt) cell.numFmt = opts.numFmt;
};

const groupSpansFor = (colDefs) => {
  const spans = [];
  colDefs.forEach((c, i) => {
    const last = spans[spans.length - 1];
    if (last && last.name === c.group) last.e = i;
    else spans.push({ name: c.group, s: i, e: i });
  });
  return spans;
};

// Configured for Portrait orientation and 1-page width scaling
const applyPrintSetup = (
  ws,
  { fitHeight = 0, titlesRow, orientation = "portrait" } = {},
) => {
  ws.pageSetup = {
    orientation: orientation, // Defaulting to 'portrait'
    paperSize: 1, // Letter size
    fitToPage: true,
    fitToWidth: 1, // Shrinks contents to fit 1 page wide in Portrait mode
    fitToHeight: fitHeight, // 0 lets long lists spill vertically across pages naturally
    horizontalCentered: true,
    verticalCentered: false,
    margins: {
      left: 0.25,
      right: 0.25,
      top: 0.4,
      bottom: 0.4,
      header: 0.2,
      footer: 0.2,
    },
  };
  if (titlesRow) ws.pageSetup.printTitlesRow = titlesRow;
};

// ── Columns definitions (optimised width for portrait mode) ────────────────
const COLS = [
  {
    header: "Store",
    key: "store",
    width: 8,
    align: "center",
    group: "Identity",
  },
  {
    header: "Sales",
    key: "sales",
    width: 14,
    align: "right",
    fmt: "accounting",
    group: "Store Performance",
  },
  {
    header: "Quota",
    key: "quota",
    width: 14,
    align: "right",
    fmt: "accounting",
    group: "Store Performance",
  },
  {
    header: "Quota %",
    key: "quotaPercent",
    width: 9,
    align: "center",
    fmt: "pct",
    group: "Store Performance",
  },
  {
    header: "UP/DOWN %",
    key: "percentChange",
    width: 10,
    align: "center",
    fmt: "pct",
    group: "Store Performance",
  },
  {
    header: "Tier",
    key: "tier",
    width: 8,
    align: "center",
    fmt: "tier",
    group: "Store Performance",
  },
  {
    header: "Comm. Rate",
    key: "commissionRate",
    width: 10,
    align: "center",
    fmt: "pct",
    group: "DM Commission",
  },
  {
    header: "Multiplier",
    key: "multiplier",
    width: 9,
    align: "center",
    fmt: "number",
    group: "DM Commission",
  },
  {
    header: "Commission",
    key: "commission",
    width: 14,
    align: "right",
    fmt: "accounting",
    group: "Total",
  },
];

const ALL_COLS = [
  {
    header: "DM Name",
    key: "dmName",
    width: 18,
    align: "left",
    group: "Identity",
  },
  ...COLS,
];

function writeTitle(
  ws,
  rowIdx,
  text,
  colSpan,
  bg = COLOR.titleBg,
  fg = COLOR.titleFg,
  size = FONT_SZ.title,
) {
  ws.getRow(rowIdx).height = ROW_HPT.title;
  for (let c = 1; c <= colSpan; c++) {
    const cell = ws.getCell(rowIdx, c);
    cell.fill = fill(bg);
    cell.border = border();
  }
  const cell = ws.getCell(rowIdx, 1);
  cell.value = text;
  cell.font = { name: FONT, bold: true, size, color: { argb: fg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  ws.mergeCells(rowIdx, 1, rowIdx, colSpan);
  return rowIdx + 1;
}

function writeBandAndHeader(ws, startRow, colDefs) {
  const bandRow = startRow;
  const headRow = startRow + 1;
  ws.getRow(bandRow).height = ROW_HPT.band;
  ws.getRow(headRow).height = ROW_HPT.head;

  groupSpansFor(colDefs).forEach((g) => {
    const meta = GROUP_META[g.name] || GROUP_META.Identity;
    for (let c = g.s; c <= g.e; c++) {
      const cell = ws.getCell(bandRow, c + 1);
      cell.value = c === g.s ? g.name : null;
      cell.fill = fill(meta.band);
      cell.font = {
        name: FONT,
        bold: true,
        size: FONT_SZ.band,
        color: { argb: meta.text },
      };
      cell.border = border();
      cell.alignment = { horizontal: "center", vertical: "middle" };
    }
    if (g.e > g.s) ws.mergeCells(bandRow, g.s + 1, bandRow, g.e + 1);
  });

  colDefs.forEach((col, ci) => {
    const meta = GROUP_META[col.group] || GROUP_META.Identity;
    const cell = ws.getCell(headRow, ci + 1);
    cell.value = col.header;
    cell.fill = fill(meta.head);
    cell.font = {
      name: FONT,
      bold: true,
      size: FONT_SZ.head,
      color: { argb: meta.text },
    };
    cell.border = border();
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
  });

  return headRow + 1;
}

function writeRows(ws, rows, startRow, colDefs) {
  rows.forEach((row, i) => {
    const r = startRow + i;
    ws.getRow(r).height = ROW_HPT.data;
    const isAlt = i % 2 === 1;
    const hitQuota = Number(row.quotaPercent || 0) >= QUOTA_HILITE;
    const rowBg = hitQuota ? COLOR.quotaHit : isAlt ? COLOR.zebra : "FFFFFF";

    colDefs.forEach((col, ci) => {
      const cell = ws.getCell(r, ci + 1);
      const raw = row[col.key];

      if (col.fmt === "currency") {
        cell.value = Number(raw || 0);
        cell.numFmt = CUR_FMT;
      } else if (col.fmt === "accounting") {
        cell.value = Number(raw || 0);
        cell.numFmt = ACCT_FMT;
      } else if (col.fmt === "number") {
        cell.value = Number(raw || 0);
        cell.numFmt = NUM_FMT;
      } else if (col.fmt === "pct") {
        cell.value = Number(raw || 0) / 100;
        cell.numFmt = PCT_FMT;
      } else if (col.fmt === "tier") {
        const n = parseFloat(raw);
        if (Number.isFinite(n)) {
          cell.value = n;
          cell.numFmt = '0"%"';
        } else {
          cell.value = raw ?? "";
        }
      } else {
        cell.value = raw ?? "";
      }

      applyDataStyle(cell, {
        align: col.align,
        bg: rowBg,
        bold: col.fmt === "accounting",
      });
    });
  });

  return startRow + rows.length;
}

// ── Footers ─────────────────────────────────────────────────────────────────
function writeTotalsFooter(
  ws,
  rows,
  footerRow,
  colDefs,
  adjustment = 0,
  bonus = 0,
) {
  const adj = Number(adjustment || 0);
  const bns = Number(bonus || 0);
  const totalCommission = rows.reduce(
    (s, r) => s + Number(r.commission || 0),
    0,
  );
  const totalSales = rows.reduce((s, r) => s + Number(r.sales || 0), 0);
  const totalQuota = rows.reduce((s, r) => s + Number(r.quota || 0), 0);
  const totalFinal = totalCommission + adj + bns;

  const numCols = colDefs.length;

  // Row 1 — Subtotals
  const totalsRow = footerRow;
  ws.getRow(totalsRow).height = ROW_HPT.data;
  colDefs.forEach((col, ci) => {
    const cell = ws.getCell(totalsRow, ci + 1);
    let val = "";
    if (col.key === "store" || col.key === "dmName") val = "SUBTOTAL";
    else if (col.key === "sales") {
      cell.numFmt = ACCT_FMT;
      val = totalSales;
    } else if (col.key === "quota") {
      cell.numFmt = ACCT_FMT;
      val = totalQuota;
    } else if (col.key === "quotaPercent") {
      cell.numFmt = PCT_FMT;
      val = totalQuota > 0 ? totalSales / totalQuota : 0;
    } else if (col.key === "commission") {
      cell.numFmt = ACCT_FMT;
      val = totalCommission;
    }
    cell.value = val;
    cell.font = {
      name: FONT,
      bold: true,
      size: FONT_SZ.total,
      color: { argb: COLOR.totalFg },
    };
    cell.fill = fill(COLOR.totalBg);
    cell.border = borderTop(COLOR.totalTop);
    cell.alignment = { horizontal: col.align, vertical: "middle" };
  });

  // Row 2 — Manual Adjustment Row
  const adjRow = totalsRow + 1;
  ws.getRow(adjRow).height = ROW_HPT.data;
  const adjLabel = ws.getCell(adjRow, 1);
  adjLabel.value = "Adjustment";
  adjLabel.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.total,
    color: { argb: COLOR.adjFg },
  };
  adjLabel.fill = fill(COLOR.adjBg);
  adjLabel.border = border();
  adjLabel.alignment = { horizontal: "left", vertical: "middle" };
  if (numCols > 1) ws.mergeCells(adjRow, 1, adjRow, numCols - 1);
  for (let c = 2; c < numCols; c++) {
    const cc = ws.getCell(adjRow, c);
    cc.fill = fill(COLOR.adjBg);
    cc.border = border();
  }
  const adjCell = ws.getCell(adjRow, numCols);
  adjCell.value = adj;
  adjCell.numFmt = ACCT_FMT;
  adjCell.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.total,
    color: { argb: COLOR.adjFg },
  };
  adjCell.fill = fill(COLOR.adjBg);
  adjCell.border = border();
  adjCell.alignment = { horizontal: "right", vertical: "middle" };

  // Row 3 — Manual Bonus Row
  const bonusRow = adjRow + 1;
  ws.getRow(bonusRow).height = ROW_HPT.data;
  const bonusLabel = ws.getCell(bonusRow, 1);
  bonusLabel.value = "Bonus";
  bonusLabel.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.total,
    color: { argb: COLOR.bonusFg },
  };
  bonusLabel.fill = fill(COLOR.bonusBg);
  bonusLabel.border = border();
  bonusLabel.alignment = { horizontal: "left", vertical: "middle" };
  if (numCols > 1) ws.mergeCells(bonusRow, 1, bonusRow, numCols - 1);
  for (let c = 2; c < numCols; c++) {
    const cc = ws.getCell(bonusRow, c);
    cc.fill = fill(COLOR.bonusBg);
    cc.border = border();
  }
  const bonusCell = ws.getCell(bonusRow, numCols);
  bonusCell.value = bns;
  bonusCell.numFmt = ACCT_FMT;
  bonusCell.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.total,
    color: { argb: COLOR.bonusFg },
  };
  bonusCell.fill = fill(COLOR.bonusBg);
  bonusCell.border = border();
  bonusCell.alignment = { horizontal: "right", vertical: "middle" };

  // Row 4 — Net Total Commission Row
  const finalRow = bonusRow + 1;
  ws.getRow(finalRow).height = ROW_HPT.data;
  const finalLabel = ws.getCell(finalRow, 1);
  finalLabel.value = "NET COMMISSION";
  finalLabel.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.final,
    color: { argb: COLOR.finalFg },
  };
  finalLabel.fill = fill(COLOR.finalBg);
  finalLabel.border = border();
  finalLabel.alignment = { horizontal: "left", vertical: "middle" };
  if (numCols > 1) ws.mergeCells(finalRow, 1, finalRow, numCols - 1);
  for (let c = 2; c < numCols; c++) {
    const cc = ws.getCell(finalRow, c);
    cc.fill = fill(COLOR.finalBg);
    cc.border = border();
  }
  const finalCell = ws.getCell(finalRow, numCols);
  finalCell.value = totalFinal;
  finalCell.numFmt = ACCT_FMT;
  finalCell.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.final,
    color: { argb: COLOR.finalFg },
  };
  finalCell.fill = fill(COLOR.finalBg);
  finalCell.border = border();
  finalCell.alignment = { horizontal: "right", vertical: "middle" };

  return finalRow + 1;
}

// ── Build DM Sheet ───────────────────────────────────────────────────────────
function buildDMSheet(
  wb,
  dmName,
  rows,
  adjustment,
  bonus,
  colDefs,
  tabColor,
  periodLabel,
  orientation,
) {
  const ws = wb.addWorksheet(dmName, {
    properties: { tabColor: { argb: tabColor } },
  });

  colDefs.forEach((col, i) => {
    ws.getColumn(i + 1).width = col.width;
  });

  const titleText =
    `District Manager Commission Report — ${dmName}` +
    (periodLabel ? `      ·      ${periodLabel}` : "");
  writeTitle(ws, 1, titleText, colDefs.length);
  const dataStart = writeBandAndHeader(ws, 2, colDefs);

  const dataEnd = writeRows(ws, rows, dataStart, colDefs);
  writeTotalsFooter(ws, rows, dataEnd, colDefs, adjustment, bonus);

  ws.views = [{ state: "frozen", ySplit: 3 }];
  applyPrintSetup(ws, { fitHeight: 0, titlesRow: "1:3", orientation });

  return ws;
}

// ── Main Export Function ──────────────────────────────────────────────────────
/**
 * @param {Array} rows - Row records
 * @param {string} [filename] - Custom output filename
 * @param {Object} [meta] - Metadata options, e.g. { periodLabel, orientation: "portrait" }
 */
export async function exportDMCommissionReportToExcel(
  rows,
  filename,
  meta = {},
) {
  const periodLabel = meta.periodLabel || "";
  const orientation = meta.orientation || "portrait"; // Defaults to portrait orientation

  const wb = new ExcelJS.Workbook();
  wb.creator = "Commission Portal";
  wb.created = new Date();

  const dmMap = new Map();
  rows.forEach((row) => {
    if (!dmMap.has(row.dmName)) dmMap.set(row.dmName, []);
    dmMap.get(row.dmName).push(row);
  });

  const allWs = wb.addWorksheet("All DMs", {
    properties: { tabColor: { argb: COLOR.titleBg } },
  });
  ALL_COLS.forEach((col, i) => {
    allWs.getColumn(i + 1).width = col.width;
  });

  const TAB_COLORS = [
    "2563EB",
    "16A34A",
    "9333EA",
    "EA580C",
    "0891B2",
    "BE185D",
    "CA8A04",
    "475569",
  ];

  let colorIdx = 0;
  dmMap.forEach((dmRows, dmName) => {
    const adjustment = dmRows[0]?.adjustment ?? 0;
    const bonus = dmRows[0]?.bonus ?? 0;
    buildDMSheet(
      wb,
      dmName,
      dmRows,
      adjustment,
      bonus,
      COLS,
      TAB_COLORS[colorIdx % TAB_COLORS.length],
      periodLabel,
      orientation,
    );
    colorIdx++;
  });

  const allTitleText =
    "District Manager Commission Report — All DMs" +
    (periodLabel ? `      ·      ${periodLabel}` : "");
  let currentRow = writeTitle(allWs, 1, allTitleText, ALL_COLS.length);

  dmMap.forEach((dmRows, dmName) => {
    const adjustment = dmRows[0]?.adjustment ?? 0;
    const bonus = dmRows[0]?.bonus ?? 0;

    allWs.getRow(currentRow).height = 22;
    for (let c = 1; c <= ALL_COLS.length; c++) {
      const cell = allWs.getCell(currentRow, c);
      cell.fill = fill(COLOR.sectionBg);
      cell.border = border();
    }
    const subCell = allWs.getCell(currentRow, 1);
    subCell.value = dmName;
    subCell.font = {
      name: FONT,
      bold: true,
      size: FONT_SZ.head,
      color: { argb: COLOR.sectionFg },
    };
    subCell.alignment = { horizontal: "left", vertical: "middle" };
    allWs.mergeCells(currentRow, 1, currentRow, ALL_COLS.length);
    currentRow++;

    currentRow = writeBandAndHeader(allWs, currentRow, ALL_COLS);
    currentRow = writeRows(allWs, dmRows, currentRow, ALL_COLS);
    currentRow = writeTotalsFooter(
      allWs,
      dmRows,
      currentRow,
      ALL_COLS,
      adjustment,
      bonus,
    );

    currentRow++;
  });

  let totalAdj = 0;
  let totalBns = 0;
  dmMap.forEach((dmRows) => {
    totalAdj += Number(dmRows[0]?.adjustment || 0);
    totalBns += Number(dmRows[0]?.bonus || 0);
  });
  const grandCommission = rows.reduce(
    (s, r) => s + Number(r.commission || 0),
    0,
  );

  allWs.getRow(currentRow).height = 24;
  for (let c = 1; c <= ALL_COLS.length; c++) {
    const cell = allWs.getCell(currentRow, c);
    cell.fill = fill(COLOR.titleBg);
    cell.border = border();
  }
  const gcCell = allWs.getCell(currentRow, 1);
  gcCell.value = "GRAND TOTAL — All DMs (Commission + Adjustments + Bonuses)";
  gcCell.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.final,
    color: { argb: "FFFFFF" },
  };
  gcCell.alignment = { horizontal: "left", vertical: "middle" };
  allWs.mergeCells(currentRow, 1, currentRow, ALL_COLS.length - 1);

  const gcVal = allWs.getCell(currentRow, ALL_COLS.length);
  gcVal.value = grandCommission + totalAdj + totalBns;
  gcVal.numFmt = ACCT_FMT;
  gcVal.font = {
    name: FONT,
    bold: true,
    size: FONT_SZ.final,
    color: { argb: "FFFFFF" },
  };
  gcVal.fill = fill(COLOR.titleBg);
  gcVal.border = border();
  gcVal.alignment = { horizontal: "right", vertical: "middle" };

  allWs.views = [{ state: "frozen", ySplit: 1 }];
  applyPrintSetup(allWs, { fitHeight: 0, titlesRow: "1:1", orientation });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(
    blob,
    filename ||
      `DM_Commission_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
  );
}

// import ExcelJS from "exceljs";
// import { saveAs } from "file-saver";

// // ── Palette (matched to ExcelExport.jsx) ─────────────────────────────────────
// const COLOR = {
//   titleBg: "1E293B", // slate banner — same as ExcelExport title row
//   titleFg: "FFFFFF",
//   zebra: "F1F5F9", // alt data row
//   quotaHit: "DCFCE7", // >=99% quota → light green row
//   totalBg: "E2E8F0", // grand-total slate band
//   totalFg: "0F172A",
//   totalTop: "94A3B8", // medium top border on the totals row
//   adjBg: "FEF3C7", // amber (matches ExcelExport "Total" group band)
//   adjFg: "92400E",
//   finalBg: "DCFCE7", // green final-total
//   finalFg: "14532D",
//   sectionBg: "475569", // slate sub-header that labels each DM on the All sheet
//   sectionFg: "FFFFFF",
//   borderColor: "CBD5E1",
// };

// // Group color bands — identical palette to ExcelExport.jsx
// const GROUP_META = {
//   Identity: { band: "E2E8F0", head: "F1F5F9", text: "334155" },
//   "Store Performance": { band: "DBEAFE", head: "EFF6FF", text: "1E40AF" },
//   "DM Commission": { band: "EDE9FE", head: "F5F3FF", text: "5B21B6" },
//   Total: { band: "FEF3C7", head: "FFFBEB", text: "92400E" },
// };

// const FONT = "Calibri";
// const FONT_SZ = {
//   title: 14,
//   band: 12,
//   head: 11,
//   data: 12,
//   total: 12,
//   final: 12,
// };
// const ROW_HPT = { title: 30, band: 26, head: 34, data: 22 };

// // ── Number formats (accounting-friendly, always 2 decimals) ──────────────────
// const CUR_FMT = "$#,##0.00_);[Red]($#,##0.00)";
// const PCT_FMT = "0.00%";
// const NUM_FMT = "0.00";
// // Accounting: $ flush-left, numbers right-aligned, dash for zero
// const ACCT_FMT =
//   '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';

// // quota threshold (quotaPercent comes in as a percent number, e.g. 80.59)
// const QUOTA_HILITE = 99;

// // ── Helpers ──────────────────────────────────────────────────────────────────
// const fill = (hex) => ({
//   type: "pattern",
//   pattern: "solid",
//   fgColor: { argb: hex },
// });

// const border = () => ({
//   top: { style: "thin", color: { argb: COLOR.borderColor } },
//   bottom: { style: "thin", color: { argb: COLOR.borderColor } },
//   left: { style: "thin", color: { argb: COLOR.borderColor } },
//   right: { style: "thin", color: { argb: COLOR.borderColor } },
// });

// // thin border with a medium top edge (used on the totals row)
// const borderTop = (topHex) => ({
//   ...border(),
//   top: { style: "medium", color: { argb: topHex } },
// });

// const applyDataStyle = (cell, opts = {}) => {
//   cell.font = {
//     name: FONT,
//     size: opts.size || FONT_SZ.data,
//     bold: opts.bold || false,
//     color: { argb: opts.color || "000000" },
//   };
//   cell.fill = fill(opts.bg || "FFFFFF");
//   cell.border = opts.border || border();
//   cell.alignment = { horizontal: opts.align || "left", vertical: "middle" };
//   if (opts.numFmt) cell.numFmt = opts.numFmt;
// };

// // Contiguous group spans for a set of column defs (for band labels + merges)
// const groupSpansFor = (colDefs) => {
//   const spans = [];
//   colDefs.forEach((c, i) => {
//     const last = spans[spans.length - 1];
//     if (last && last.name === c.group) last.e = i;
//     else spans.push({ name: c.group, s: i, e: i });
//   });
//   return spans;
// };

// // Fit the sheet to a single printed page width (landscape). fitHeight=1 forces
// // the whole sheet onto one page; fitHeight=0 fits the width and lets long
// // sheets flow onto additional pages. titlesRow (e.g. "1:3") repeats those rows
// // at the top of every printed page.
// const applyPrintSetup = (ws, { fitHeight = 1, titlesRow } = {}) => {
//   ws.pageSetup = {
//     orientation: "landscape",
//     paperSize: 1, // Letter
//     fitToPage: true,
//     fitToWidth: 1,
//     fitToHeight: fitHeight,
//     horizontalCentered: true,
//     margins: {
//       left: 0.3,
//       right: 0.3,
//       top: 0.4,
//       bottom: 0.4,
//       header: 0.2,
//       footer: 0.2,
//     },
//   };
//   if (titlesRow) ws.pageSetup.printTitlesRow = titlesRow;
// };

// // ── Column definitions (each tagged with a color group) ──────────────────────
// const COLS = [
//   {
//     header: "Store",
//     key: "store",
//     width: 10,
//     align: "center",
//     group: "Identity",
//   },
//   {
//     header: "Sales",
//     key: "sales",
//     width: 16,
//     align: "right",
//     fmt: "accounting",
//     group: "Store Performance",
//   },
//   {
//     header: "Quota",
//     key: "quota",
//     width: 16,
//     align: "right",
//     fmt: "accounting",
//     group: "Store Performance",
//   },
//   {
//     header: "Quota %",
//     key: "quotaPercent",
//     width: 11,
//     align: "center",
//     fmt: "pct",
//     group: "Store Performance",
//   },
//   {
//     header: "UP/DOWN %",
//     key: "percentChange",
//     width: 12,
//     align: "center",
//     fmt: "pct",
//     group: "Store Performance",
//   },
//   {
//     header: "Tier",
//     key: "tier",
//     width: 9,
//     align: "center",
//     fmt: "tier",
//     group: "Store Performance",
//   },
//   {
//     header: "Comm. Rate",
//     key: "commissionRate",
//     width: 12,
//     align: "center",
//     fmt: "pct",
//     group: "DM Commission",
//   },
//   {
//     header: "Multiplier",
//     key: "multiplier",
//     width: 11,
//     align: "center",
//     fmt: "number",
//     group: "DM Commission",
//   },
//   {
//     header: "Commission",
//     key: "commission",
//     width: 16,
//     align: "right",
//     fmt: "accounting",
//     group: "Total",
//   },
// ];

// // "All" sheet has an extra DM Name column up front
// const ALL_COLS = [
//   {
//     header: "DM Name",
//     key: "dmName",
//     width: 22,
//     align: "left",
//     group: "Identity",
//   },
//   ...COLS,
// ];

// // ── Title banner (slate, white, centered, merged across all columns) ─────────
// function writeTitle(
//   ws,
//   rowIdx,
//   text,
//   colSpan,
//   bg = COLOR.titleBg,
//   fg = COLOR.titleFg,
//   size = FONT_SZ.title,
// ) {
//   ws.getRow(rowIdx).height = ROW_HPT.title;
//   for (let c = 1; c <= colSpan; c++) {
//     const cell = ws.getCell(rowIdx, c);
//     cell.fill = fill(bg);
//     cell.border = border();
//   }
//   const cell = ws.getCell(rowIdx, 1);
//   cell.value = text;
//   cell.font = { name: FONT, bold: true, size, color: { argb: fg } };
//   cell.alignment = { horizontal: "center", vertical: "middle" };
//   ws.mergeCells(rowIdx, 1, rowIdx, colSpan);
//   return rowIdx + 1;
// }

// // ── Group band row + colored header row ──────────────────────────────────────
// function writeBandAndHeader(ws, startRow, colDefs) {
//   const bandRow = startRow;
//   const headRow = startRow + 1;
//   ws.getRow(bandRow).height = ROW_HPT.band;
//   ws.getRow(headRow).height = ROW_HPT.head;

//   // band: one merged, colored label per contiguous group
//   groupSpansFor(colDefs).forEach((g) => {
//     const meta = GROUP_META[g.name] || GROUP_META.Identity;
//     for (let c = g.s; c <= g.e; c++) {
//       const cell = ws.getCell(bandRow, c + 1);
//       cell.value = c === g.s ? g.name : null;
//       cell.fill = fill(meta.band);
//       cell.font = {
//         name: FONT,
//         bold: true,
//         size: FONT_SZ.band,
//         color: { argb: meta.text },
//       };
//       cell.border = border();
//       cell.alignment = { horizontal: "center", vertical: "middle" };
//     }
//     if (g.e > g.s) ws.mergeCells(bandRow, g.s + 1, bandRow, g.e + 1);
//   });

//   // header: each cell tinted with its group's head color + group text color
//   colDefs.forEach((col, ci) => {
//     const meta = GROUP_META[col.group] || GROUP_META.Identity;
//     const cell = ws.getCell(headRow, ci + 1);
//     cell.value = col.header;
//     cell.fill = fill(meta.head);
//     cell.font = {
//       name: FONT,
//       bold: true,
//       size: FONT_SZ.head,
//       color: { argb: meta.text },
//     };
//     cell.border = border();
//     cell.alignment = {
//       horizontal: "center",
//       vertical: "middle",
//       wrapText: true,
//     };
//   });

//   return headRow + 1;
// }

// // ── Write data rows ──────────────────────────────────────────────────────────
// function writeRows(ws, rows, startRow, colDefs) {
//   rows.forEach((row, i) => {
//     const r = startRow + i;
//     ws.getRow(r).height = ROW_HPT.data;
//     const isAlt = i % 2 === 1;
//     const hitQuota = Number(row.quotaPercent || 0) >= QUOTA_HILITE;
//     const rowBg = hitQuota ? COLOR.quotaHit : isAlt ? COLOR.zebra : "FFFFFF";

//     colDefs.forEach((col, ci) => {
//       const cell = ws.getCell(r, ci + 1);
//       const raw = row[col.key];

//       if (col.fmt === "currency") {
//         cell.value = Number(raw || 0);
//         cell.numFmt = CUR_FMT;
//       } else if (col.fmt === "accounting") {
//         cell.value = Number(raw || 0);
//         cell.numFmt = ACCT_FMT;
//       } else if (col.fmt === "number") {
//         cell.value = Number(raw || 0);
//         cell.numFmt = NUM_FMT;
//       } else if (col.fmt === "pct") {
//         cell.value = Number(raw || 0) / 100;
//         cell.numFmt = PCT_FMT;
//       } else if (col.fmt === "tier") {
//         // Tier like "30%" / "-20%" → store as a real number so Excel doesn't
//         // flag it as "number stored as text". Shown with a trailing %.
//         const n = parseFloat(raw);
//         if (Number.isFinite(n)) {
//           cell.value = n;
//           cell.numFmt = '0"%"';
//         } else {
//           cell.value = raw ?? "";
//         }
//       } else {
//         cell.value = raw ?? "";
//       }

//       applyDataStyle(cell, {
//         align: col.align,
//         bg: rowBg,
//         bold: col.fmt === "accounting",
//       });
//     });
//   });

//   return startRow + rows.length;
// }

// // ── Write totals + adjustment + final total footer ───────────────────────────
// function writeTotalsFooter(ws, rows, footerRow, colDefs, adjustment) {
//   const adj = Number(adjustment || 0);
//   const totalCommission = rows.reduce(
//     (s, r) => s + Number(r.commission || 0),
//     0,
//   );
//   const totalSales = rows.reduce((s, r) => s + Number(r.sales || 0), 0);
//   const totalQuota = rows.reduce((s, r) => s + Number(r.quota || 0), 0);
//   const totalFinal = totalCommission + adj;

//   const numCols = colDefs.length;

//   // ── Gap row before the totals block ──
//   const gapRow = footerRow;
//   ws.getRow(gapRow).height = 12;

//   // Row 1 — Totals (slate band, medium top border — matches ExcelExport)
//   const totalsRow = footerRow + 1;
//   ws.getRow(totalsRow).height = ROW_HPT.data;
//   colDefs.forEach((col, ci) => {
//     const cell = ws.getCell(totalsRow, ci + 1);
//     let val = "";
//     if (col.key === "store" || col.key === "dmName") val = "TOTALS";
//     else if (col.key === "sales") {
//       cell.numFmt = ACCT_FMT;
//       val = totalSales;
//     } else if (col.key === "quota") {
//       cell.numFmt = ACCT_FMT;
//       val = totalQuota;
//     } else if (col.key === "quotaPercent") {
//       cell.numFmt = PCT_FMT;
//       val = totalQuota > 0 ? totalSales / totalQuota : 0;
//     } else if (col.key === "commission") {
//       cell.numFmt = ACCT_FMT;
//       val = totalCommission;
//     }
//     cell.value = val;
//     cell.font = {
//       name: FONT,
//       bold: true,
//       size: FONT_SZ.total,
//       color: { argb: COLOR.totalFg },
//     };
//     cell.fill = fill(COLOR.totalBg);
//     cell.border = borderTop(COLOR.totalTop);
//     cell.alignment = { horizontal: col.align, vertical: "middle" };
//   });

//   // Row 2 — Adjustment (bonus) — amber
//   const adjRow = totalsRow + 1;
//   ws.getRow(adjRow).height = ROW_HPT.data;
//   const adjLabel = ws.getCell(adjRow, 1);
//   adjLabel.value = "Adjustment / Bonus";
//   adjLabel.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.total,
//     color: { argb: COLOR.adjFg },
//   };
//   adjLabel.fill = fill(COLOR.adjBg);
//   adjLabel.border = border();
//   adjLabel.alignment = { horizontal: "left", vertical: "middle" };
//   if (numCols > 1) ws.mergeCells(adjRow, 1, adjRow, numCols - 1);
//   for (let c = 2; c < numCols; c++) {
//     const cc = ws.getCell(adjRow, c);
//     cc.fill = fill(COLOR.adjBg);
//     cc.border = border();
//   }
//   const adjCell = ws.getCell(adjRow, numCols);
//   adjCell.value = adj;
//   adjCell.numFmt = ACCT_FMT;
//   adjCell.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.total,
//     color: { argb: COLOR.adjFg },
//   };
//   adjCell.fill = fill(COLOR.adjBg);
//   adjCell.border = border();
//   adjCell.alignment = { horizontal: "right", vertical: "middle" };

//   // Row 3 — Final Total — green
//   const finalRow = totalsRow + 2;
//   ws.getRow(finalRow).height = ROW_HPT.data;
//   const finalLabel = ws.getCell(finalRow, 1);
//   finalLabel.value = "TOTAL COMMISSION (incl. Adjustment)";
//   finalLabel.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.final,
//     color: { argb: COLOR.finalFg },
//   };
//   finalLabel.fill = fill(COLOR.finalBg);
//   finalLabel.border = border();
//   finalLabel.alignment = { horizontal: "left", vertical: "middle" };
//   if (numCols > 1) ws.mergeCells(finalRow, 1, finalRow, numCols - 1);
//   for (let c = 2; c < numCols; c++) {
//     const cc = ws.getCell(finalRow, c);
//     cc.fill = fill(COLOR.finalBg);
//     cc.border = border();
//   }
//   const finalCell = ws.getCell(finalRow, numCols);
//   finalCell.value = totalFinal;
//   finalCell.numFmt = ACCT_FMT;
//   finalCell.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.final,
//     color: { argb: COLOR.finalFg },
//   };
//   finalCell.fill = fill(COLOR.finalBg);
//   finalCell.border = border();
//   finalCell.alignment = { horizontal: "right", vertical: "middle" };

//   return finalRow + 1;
// }

// // ── Build one DM sheet ───────────────────────────────────────────────────────
// function buildDMSheet(
//   wb,
//   dmName,
//   rows,
//   adjustment,
//   colDefs,
//   tabColor,
//   periodLabel,
// ) {
//   const ws = wb.addWorksheet(dmName, {
//     properties: { tabColor: { argb: tabColor } },
//   });

//   colDefs.forEach((col, i) => {
//     ws.getColumn(i + 1).width = col.width;
//   });

//   // Row 1: title banner · Row 2-3: group band + headers
//   const titleText =
//     `District Manager Commission Report — ${dmName}` +
//     (periodLabel ? `      ·      ${periodLabel}` : "");
//   writeTitle(ws, 1, titleText, colDefs.length);
//   const dataStart = writeBandAndHeader(ws, 2, colDefs); // returns 4

//   const dataEnd = writeRows(ws, rows, dataStart, colDefs);
//   writeTotalsFooter(ws, rows, dataEnd, colDefs, adjustment);

//   // Freeze the title + band + header; repeat them on every printed page
//   ws.views = [{ state: "frozen", ySplit: 3 }];
//   applyPrintSetup(ws, { fitHeight: 1, titlesRow: "1:3" });

//   return ws;
// }

// // ── Main export function ─────────────────────────────────────────────────────
// /**
//  * exportDMCommissionReportToExcel
//  *
//  * @param {Array} rows - flat array of row objects (one per store per DM):
//  *   { store, sales, quota, quotaPercent, percentChange, tier, multiplier,
//  *     commissionRate, commission, dmName, adjustment }
//  *   NOTE: `adjustment` is the DM-level bonus (same value for all rows of a DM).
//  * @param {string} [filename] - optional filename override
//  * @param {Object} [meta] - optional { periodLabel } shown in the title banners
//  */
// export async function exportDMCommissionReportToExcel(
//   rows,
//   filename,
//   meta = {},
// ) {
//   const periodLabel = meta.periodLabel || "";
//   const wb = new ExcelJS.Workbook();
//   wb.creator = "Commission Portal";
//   wb.created = new Date();

//   // ── Group rows by DM ──
//   const dmMap = new Map();
//   rows.forEach((row) => {
//     if (!dmMap.has(row.dmName)) dmMap.set(row.dmName, []);
//     dmMap.get(row.dmName).push(row);
//   });

//   // ── "All" sheet — created FIRST so it becomes the front tab ──
//   const allWs = wb.addWorksheet("All DMs", {
//     properties: { tabColor: { argb: COLOR.titleBg } },
//   });
//   ALL_COLS.forEach((col, i) => {
//     allWs.getColumn(i + 1).width = col.width;
//   });

//   // Tab colors cycle
//   const TAB_COLORS = [
//     "2563EB",
//     "16A34A",
//     "9333EA",
//     "EA580C",
//     "0891B2",
//     "BE185D",
//     "CA8A04",
//     "475569",
//   ];

//   // ── Per-DM sheets (added after All DMs, so they follow it) ──
//   let colorIdx = 0;
//   dmMap.forEach((dmRows, dmName) => {
//     const adjustment = dmRows[0]?.adjustment ?? 0; // same for all rows of this DM
//     buildDMSheet(
//       wb,
//       dmName,
//       dmRows,
//       adjustment,
//       COLS,
//       TAB_COLORS[colorIdx % TAB_COLORS.length],
//       periodLabel,
//     );
//     colorIdx++;
//   });

//   // ── Populate the All sheet ──
//   const allTitleText =
//     "District Manager Commission Report — All DMs" +
//     (periodLabel ? `      ·      ${periodLabel}` : "");
//   let currentRow = writeTitle(allWs, 1, allTitleText, ALL_COLS.length); // -> 2

//   // Per-DM sub-sections: slate section label, then band + headers + data + footer
//   dmMap.forEach((dmRows, dmName) => {
//     const adjustment = dmRows[0]?.adjustment ?? 0;

//     allWs.getRow(currentRow).height = 22;
//     for (let c = 1; c <= ALL_COLS.length; c++) {
//       const cell = allWs.getCell(currentRow, c);
//       cell.fill = fill(COLOR.sectionBg);
//       cell.border = border();
//     }
//     const subCell = allWs.getCell(currentRow, 1);
//     subCell.value = dmName;
//     subCell.font = {
//       name: FONT,
//       bold: true,
//       size: FONT_SZ.head,
//       color: { argb: COLOR.sectionFg },
//     };
//     subCell.alignment = { horizontal: "left", vertical: "middle" };
//     allWs.mergeCells(currentRow, 1, currentRow, ALL_COLS.length);
//     currentRow++;

//     currentRow = writeBandAndHeader(allWs, currentRow, ALL_COLS);
//     currentRow = writeRows(allWs, dmRows, currentRow, ALL_COLS);
//     currentRow = writeTotalsFooter(
//       allWs,
//       dmRows,
//       currentRow,
//       ALL_COLS,
//       adjustment,
//     );

//     currentRow++; // spacer between DMs
//   });

//   // Grand total across all DMs
//   let totalAdj = 0;
//   dmMap.forEach((dmRows) => {
//     totalAdj += Number(dmRows[0]?.adjustment || 0);
//   });
//   const grandCommission = rows.reduce(
//     (s, r) => s + Number(r.commission || 0),
//     0,
//   );

//   allWs.getRow(currentRow).height = 24;
//   for (let c = 1; c <= ALL_COLS.length; c++) {
//     const cell = allWs.getCell(currentRow, c);
//     cell.fill = fill(COLOR.titleBg);
//     cell.border = border();
//   }
//   const gcCell = allWs.getCell(currentRow, 1);
//   gcCell.value = "GRAND TOTAL — All DMs (Commission + All Adjustments)";
//   gcCell.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.final,
//     color: { argb: "FFFFFF" },
//   };
//   gcCell.alignment = { horizontal: "left", vertical: "middle" };
//   allWs.mergeCells(currentRow, 1, currentRow, ALL_COLS.length - 1);

//   const gcVal = allWs.getCell(currentRow, ALL_COLS.length);
//   gcVal.value = grandCommission + totalAdj;
//   gcVal.numFmt = ACCT_FMT;
//   gcVal.font = {
//     name: FONT,
//     bold: true,
//     size: FONT_SZ.final,
//     color: { argb: "FFFFFF" },
//   };
//   gcVal.fill = fill(COLOR.titleBg);
//   gcVal.border = border();
//   gcVal.alignment = { horizontal: "right", vertical: "middle" };

//   // Freeze the All-DMs title; repeat it at the top of every printed page
//   allWs.views = [{ state: "frozen", ySplit: 1 }];
//   applyPrintSetup(allWs, { fitHeight: 0, titlesRow: "1:1" });

//   // ── Save ──
//   const buffer = await wb.xlsx.writeBuffer();
//   const blob = new Blob([buffer], {
//     type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
//   });
//   saveAs(
//     blob,
//     filename ||
//       `DM_Commission_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
//   );
// }
